/** Accessible, optional type-ahead for entity indexes (sites#392).
 * The submitted search remains authoritative; an outage here never blocks it.
 */
import { canonicalPath } from '@electinfo/common';
import { currentCycle, withCycle } from './cycle-facet';

export type SuggestType = 'Candidate' | 'Committee' | 'Party';
export interface EntitySuggestion {
  id: string;
  type: SuggestType;
  name: string;
  fecId?: string | null;
  state?: string | null;
  office?: string | null;
  district?: string | null;
  party?: string | null;
  totalFunds?: number | null;
  fundsBasis?: string | null;
}

// Fixed literal: build.js adds this document to the visitor-token allowlist.
export const ENTITY_SUGGEST_QUERY = `
query EntityTypeahead($prefix: String!, $types: [EntitySuggestType!], $cycle: Int) {
  entitySuggest(prefix: $prefix, types: $types, cycle: $cycle, limit: 8) {
    id type name fecId state office district party totalFunds fundsBasis
  }
}
`;
export const SUGGEST_DELAY_MS = 250;
export const MIN_PREFIX_LENGTH = 2;

export function suggestTypes(entityType: string): SuggestType[] | null {
  switch (entityType) {
    case 'Politician': case 'Candidate': return ['Candidate'];
    case 'Committee': return ['Committee'];
    case 'Party': return ['Party'];
    default: return null;
  }
}

export function nextActiveIndex(index: number, key: string, count: number): number {
  if (!count) return -1;
  if (key === 'ArrowDown') return (index + 1) % count;
  if (key === 'ArrowUp') return index <= 0 ? count - 1 : index - 1;
  return index;
}

export function suggestionContext(s: EntitySuggestion): string {
  const offices: Record<string, string> = { P: 'President', S: 'Senate', H: 'House' };
  const location = [s.state, s.district].filter(Boolean).join('-');
  const context = [location, offices[s.office || ''] || s.office, s.party].filter(Boolean);
  if (s.totalFunds != null && Number.isFinite(s.totalFunds)) {
    const amount = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(s.totalFunds);
    context.push(`${amount} ${s.fundsBasis === 'contributions' ? 'contributions' : 'raised'} (all cycles)`);
  }
  return context.join(' · ');
}

/** The existing URL helper uses the serving UUID, never a guessed name/FEC slug.
 * Candidate is the page type; Politician is only the browse/API label.
 */
export function suggestionPath(s: EntitySuggestion): string {
  return `/${canonicalPath(s.type, s.id)}/`;
}

/** us-pages supplies its published vanity URL in the HTTP Link header, even
 * when reached via the UUID storage path. Keep preview navigation on preview.
 * Only accept a canonical in the same collection and on a known site origin.
 */
export function publishedSuggestionPath(link: string | null, storage: string, origin: string): string {
  const match = link?.match(/<([^>]+)>;\s*rel="?canonical"?/i);
  if (!match) return storage;
  try {
    const url = new URL(match[1], origin);
    const collection = storage.split('/')[1];
    if ((url.origin !== origin && url.origin !== 'https://us.elect.info' && url.origin !== 'https://preview.elect.info') ||
        !url.pathname.startsWith(`/${collection}/`) || url.username || url.password) return storage;
    return url.pathname;
  } catch { return storage; }
}

async function navigateSuggestion(s: EntitySuggestion): Promise<void> {
  let path = suggestionPath(s);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 1500);
  try {
    // One bounded read on selection, not a renderer/graph request for every hit.
    const response = await fetch(path, { method: 'HEAD', signal: controller.signal });
    if (response.ok) path = publishedSuggestionPath(response.headers.get('link'), path, window.location.origin);
  } catch { /* UUID storage paths are always a valid fallback. */ }
  finally { clearTimeout(timer); }
  window.location.assign(withCycle(path, currentCycle()));
}

/** Cancellation + sequence checks protect against transports that ignore abort. */
export class SuggestionRequest {
  private sequence = 0;
  private controller: AbortController | null = null;
  cancel(): void {
    this.sequence++;
    this.controller?.abort();
    this.controller = null;
  }
  async load(prefix: string, types: SuggestType[], cycle: number | null, endpoint: string): Promise<EntitySuggestion[] | null> {
    this.cancel();
    const sequence = this.sequence;
    this.controller = new AbortController();
    try {
      const response = await fetch(endpoint, {
        method: 'POST', signal: this.controller.signal,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: ENTITY_SUGGEST_QUERY, variables: { prefix, types, ...(cycle !== null ? { cycle } : {}) } })
      });
      if (!response.ok) throw new Error('Suggestions unavailable');
      const json = await response.json();
      if (json.errors?.length) throw new Error('Suggestions unavailable');
      if (sequence !== this.sequence) return null;
      return (json.data?.entitySuggest ?? []).filter((s: EntitySuggestion) =>
        types.includes(s.type) && typeof s.id === 'string' && /^[a-z0-9-]+$/i.test(s.id) && typeof s.name === 'string'
      );
    } catch (error) {
      if (sequence !== this.sequence) return null;
      throw error;
    }
  }
}

let instance = 0;
export class EntityTypeahead {
  private readonly requests = new SuggestionRequest();
  private readonly list = document.createElement('ul');
  private readonly status = document.createElement('span');
  private readonly wrapper = document.createElement('div');
  private timer: ReturnType<typeof setTimeout> | undefined;
  private suggestions: EntitySuggestion[] = [];
  private active = -1;
  private composing = false;
  private navigating = false;

  constructor(private input: HTMLInputElement, private options: {
    types: SuggestType[]; cycle: () => number | null; endpoint?: string;
  }) {
    const id = `entity-suggestions-${++instance}`;
    this.wrapper.className = 'entity-typeahead';
    input.before(this.wrapper);
    this.wrapper.append(input, this.list, this.status);
    this.list.id = id;
    this.list.className = 'entity-suggestions';
    this.list.setAttribute('role', 'listbox');
    this.list.setAttribute('aria-label', 'Entity suggestions');
    this.status.id = `${id}-status`;
    this.status.className = 'entity-suggest-status';
    this.status.setAttribute('role', 'status');
    this.status.setAttribute('aria-live', 'polite');
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-label', input.getAttribute('aria-label') || 'Search entities');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-controls', id);
    input.setAttribute('aria-describedby', this.status.id);
    this.close();
    if (!document.getElementById('entity-suggest-styles')) {
      const style = document.createElement('style');
      style.id = 'entity-suggest-styles';
      style.textContent = `
        .entity-typeahead { position: relative; flex: 1; min-width: 0; }
        .entity-search-form .entity-typeahead input { width: 100%; box-sizing: border-box; }
        .entity-suggestions { position: absolute; top: 100%; left: 0; right: 0; z-index: 20; max-height: 24rem; overflow-y: auto; margin: 4px 0 0; padding: 4px; list-style: none; border: 1px solid #cbd5e0; border-radius: 6px; background: white; color: #1a202c; box-shadow: 0 6px 18px #0002; text-align: left; }
        .entity-suggestions [role=option] { padding: 10px; border-radius: 4px; cursor: pointer; overflow-wrap: anywhere; }
        .entity-suggestions [aria-selected=true], .entity-suggestions [role=option]:hover { background: #edf2f7; outline: 2px solid #2c5282; outline-offset: -2px; }
        .entity-suggest-badge { display: inline-block; font-size: .75rem; font-weight: 600; padding: 2px 6px; border-radius: 4px; background: #e2e8f0; margin-right: 8px; }
        .entity-suggest-context { display: block; margin-top: 4px; font-size: .85rem; color: #4a5568; }
        .entity-suggest-status { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); }
      `;
      document.head.append(style);
    }
    input.addEventListener('input', () => this.schedule());
    input.addEventListener('compositionstart', () => { this.composing = true; this.close(); });
    input.addEventListener('compositionend', () => { this.composing = false; this.schedule(); });
    input.addEventListener('keydown', event => this.keydown(event));
    input.addEventListener('blur', () => this.close());
    // Keep focus on the combobox until click (including touch/pen). Pointer
    // events preserve native touch panning, unlike cancelling touchstart.
    this.list.addEventListener('pointerdown', event => event.preventDefault());
    input.form?.addEventListener('submit', () => this.close());
    window.addEventListener('popstate', () => this.close());
    // A back/forward-cache restore retains this instance, including its
    // navigation guard. Make the input usable again when returning to it.
    window.addEventListener('pageshow', () => { this.navigating = false; this.close(); });
  }

  close(): void {
    clearTimeout(this.timer);
    this.requests.cancel();
    this.suggestions = [];
    this.active = -1;
    this.list.replaceChildren();
    this.list.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.status.textContent = '';
  }

  private schedule(): void {
    this.close();
    const prefix = this.input.value.trim();
    if (this.composing || prefix.length < MIN_PREFIX_LENGTH) return;
    this.timer = setTimeout(() => void this.load(prefix), SUGGEST_DELAY_MS);
  }

  private async load(prefix: string): Promise<void> {
    this.status.textContent = 'Loading suggestions';
    try {
      const suggestions = await this.requests.load(prefix, this.options.types, this.options.cycle(), this.options.endpoint || '/api/graphql');
      if (suggestions === null) return;
      this.suggestions = suggestions;
      this.list.replaceChildren(...suggestions.map((s, index) => {
        const option = document.createElement('li');
        option.id = `${this.list.id}-${index}`;
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', 'false');
        const badge = document.createElement('span');
        badge.className = 'entity-suggest-badge';
        badge.textContent = s.type;
        const name = document.createElement('strong');
        name.textContent = s.name;
        const context = document.createElement('span');
        context.className = 'entity-suggest-context';
        context.textContent = suggestionContext(s);
        option.append(badge, name, context);
        option.addEventListener('click', () => void this.select(s));
        return option;
      }));
      this.list.hidden = !suggestions.length;
      this.input.setAttribute('aria-expanded', String(!!suggestions.length));
      this.status.textContent = suggestions.length
        ? `${suggestions.length} suggestions. Use up and down arrows to choose, Enter to open; or submit Search for all results.`
        : 'No suggestions. Submit Search for all results.';
    } catch {
      this.close();
      this.status.textContent = 'Suggestions unavailable. Submit Search for results.';
    }
  }

  private keydown(event: KeyboardEvent): void {
    if (event.isComposing || this.composing) return;
    if (event.key === 'Escape') { this.close(); return; }
    if (this.list.hidden) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.active = nextActiveIndex(this.active, event.key, this.suggestions.length);
      Array.from(this.list.children).forEach((option, index) => option.setAttribute('aria-selected', String(index === this.active)));
      const option = this.list.children[this.active] as HTMLElement;
      this.input.setAttribute('aria-activedescendant', option.id);
      option.scrollIntoView({ block: 'nearest' });
    } else if (event.key === 'Enter' && this.active >= 0) {
      event.preventDefault();
      void this.select(this.suggestions[this.active]);
    } else if (event.key === 'Tab') this.close();
  }

  private async select(suggestion: EntitySuggestion): Promise<void> {
    if (this.navigating) return;
    this.navigating = true;
    this.close();
    this.status.textContent = `Opening ${suggestion.name}`;
    await navigateSuggestion(suggestion);
  }
}
